'use client';

import { useAuth } from '@/hooks/useAuth';
import { Alert } from '@/components/common/Alert';
import { TournamentAuthGuard } from '@/components/tournament/TournamentAuthGuard';
import type { Tournament, Team } from '@/lib/types/auction';

interface EditAuthGuardProps {
  children: React.ReactNode;
  tournament: Tournament;
  entity: 'tournament' | 'team' | 'pool';
  team?: Team; // Required when entity is 'team'
}

export function EditAuthGuard({
  children,
  tournament,
  entity,
  team,
}: EditAuthGuardProps) {
  const { user } = useAuth();

  // Entity-specific permission checks
  const checkEntityPermissions = () => {
    switch (entity) {
      case 'tournament':
        return true; // TournamentAuthGuard will handle this
      case 'pool':
        return true; // TournamentAuthGuard will handle this
      case 'team':
        if (!team) {
          throw new Error('Team prop is required when entity is "team"');
        }
        // Allow if user is team owner or tournament owner
        const isTeamOwner = team.owner?.id === user?.id;
        const isTournamentOwner = tournament.owner?.id === user?.id;
        if (!isTeamOwner && !isTournamentOwner) {
          return (
            <Alert
              type="error"
              message="Only the team owner or tournament owner can edit team details."
            />
          );
        }
        return true;
    }
  };

  const permissionCheck = checkEntityPermissions();
  if (permissionCheck !== true) {
    return permissionCheck;
  }

  // Use TournamentAuthGuard for base authentication and tournament status checks
  return (
    <TournamentAuthGuard
      tournament={tournament}
      allowedStatuses={['DRAFT', 'REGISTRATION']}
      requireOwner={entity !== 'team'} // Only require tournament ownership for non-team entities
    >
      {children}
    </TournamentAuthGuard>
  );
}
